import React, { useMemo, useCallback, useEffect, useState } from 'react';
import type {
  AgentChatModelOption,
  AgentChatQuotaSnapshot,
  ChatModelId,
} from './agentChatSharedTypes';
import { slotRemainingGenerations, formatQuotaResetCountdown } from './agentChatSharedTypes';
import './AgentModelControls.css';

export type { ChatModelId } from './agentChatSharedTypes';

/** Классы Font Awesome для пунктов меню и переключателя модели */
export const AGENT_MODEL_FA_ICON_CLASS: Record<ChatModelId, string> = {
  premium: 'fa-solid fa-coins',
  premium_pro: 'fa-solid fa-crown',
};

export const MODEL_CYCLE_ORDER: ChatModelId[] = ['premium', 'premium_pro'];

/** Подписи по умолчанию до ответа `GET /chat/models`. */
export const DEFAULT_MODELS: AgentChatModelOption[] = [
  { id: 'premium', label: 'Премиум', can_stop: true },
  { id: 'premium_pro', label: 'Премиум PRO', can_stop: true },
];

export function nextModel(current: ChatModelId): ChatModelId {
  const idx = MODEL_CYCLE_ORDER.indexOf(current);
  return MODEL_CYCLE_ORDER[(idx + 1) % MODEL_CYCLE_ORDER.length];
}

export function AgentModelControls({
  chatModels,
  chatModelId,
  onCycleModel,
  disabled,
  showLoader,
  quota,
}: {
  chatModels: AgentChatModelOption[];
  chatModelId: ChatModelId;
  onCycleModel: () => void;
  disabled: boolean;
  /** Лоадер справа от названия типа (стрим / загрузка истории). */
  showLoader?: boolean;
  quota: AgentChatQuotaSnapshot | null;
}): React.ReactNode {
  const [quotaTick, setQuotaTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setQuotaTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, []);

  const quotaResetHint = useMemo(() => {
    void quotaTick;
    if (!quota?.reset_at) return null;
    return formatQuotaResetCountdown(quota.reset_at, Date.now());
  }, [quota?.reset_at, quotaTick]);
  const currentLabel = useMemo(() => {
    const current = chatModels.find((m) => m.id === chatModelId);
    const labels: Record<ChatModelId, string> = {
      premium: 'Премиум',
      premium_pro: 'Премиум PRO',
    };
    return current?.label ?? labels[chatModelId];
  }, [chatModels, chatModelId]);

  const particleVariant = chatModelId === 'premium_pro' ? 'premiumPro' : 'premium';

  const premiumParticlesConfig = useMemo(() => {
    if (!particleVariant) return [];
    const count = particleVariant === 'premiumPro' ? 18 : 14;
    const halfW = particleVariant === 'premiumPro' ? 48 : 42;
    const halfH = particleVariant === 'premiumPro' ? 18 : 16;
    return Array.from({ length: count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const startX = halfW * Math.cos(angle);
      const startY = halfH * Math.sin(angle);
      const flyAngle = angle + (Math.random() - 0.5) * (particleVariant === 'premiumPro' ? 0.75 : 0.6);
      const flyDist =
        particleVariant === 'premiumPro' ? 18 + Math.random() * 40 : 15 + Math.random() * 35;
      const dx = Math.cos(flyAngle) * flyDist;
      const dy = Math.sin(flyAngle) * flyDist;
      return {
        startX,
        startY,
        dx,
        dy,
        delay: -Math.random() * 2.8,
        duration: particleVariant === 'premiumPro' ? 1 + Math.random() * 1.4 : 1.1 + Math.random() * 1.6,
      };
    });
  }, [particleVariant]);

  const handleClick = useCallback(() => {
    if (!disabled) onCycleModel();
  }, [disabled, onCycleModel]);

  const remaining = useMemo(
    () => slotRemainingGenerations(quota, chatModelId),
    [quota, chatModelId]
  );

  const slotPayload = quota ? quota[chatModelId] : null;

  const dailyRemaining = slotPayload?.daily_remaining ?? null;
  const bonusRemaining = slotPayload?.bonus_remaining ?? null;

  const shouldShowBadge = true;

  const dailyBadgeText =
    quota == null
      ? '…'
      : remaining === null
        ? '∞'
        : String(Math.max(0, typeof dailyRemaining === 'number' ? dailyRemaining : 0));

  const showBonusBadge =
    shouldShowBadge &&
    quota != null &&
    remaining !== null &&
    bonusRemaining != null &&
    bonusRemaining > 0;

  const titleBase = `Модель: ${currentLabel}. Нажми для переключения`;
  const quotaHintParts: string[] = [];
  if (quota && quotaResetHint) quotaHintParts.push(quotaResetHint);
  if (quota && remaining !== null && dailyRemaining != null && bonusRemaining != null) {
    if (bonusRemaining > 0) {
      quotaHintParts.push(`Ежедневные: ${dailyRemaining}, Бонусные: +${bonusRemaining}`);
    }
  }
  const buttonTitle =
    quotaHintParts.length > 0 ? `${titleBase}. ${quotaHintParts.join('. ')}` : titleBase;

  return (
    <div
      className={`chatAssistant__modelBtnWrap ${
        chatModelId === 'premium'
          ? 'chatAssistant__modelBtnWrap_premium'
          : chatModelId === 'premium_pro'
            ? 'chatAssistant__modelBtnWrap_premiumPro'
            : ''
      }`}
    >
      {particleVariant && (
        <div
          className={`chatAssistant__modelBtn_particles ${
            particleVariant === 'premiumPro'
              ? 'chatAssistant__modelBtn_particles_premiumPro'
              : 'chatAssistant__modelBtn_particles_premium'
          }`}
          aria-hidden="true"
        >
          {premiumParticlesConfig.map((p, i) => (
            <span
              key={i}
              className="chatAssistant__modelBtn_particle"
              style={
                {
                  '--startX': `${p.startX}px`,
                  '--startY': `${p.startY}px`,
                  '--dx': `${p.dx}px`,
                  '--dy': `${p.dy}px`,
                  '--delay': `${p.delay}s`,
                  '--duration': `${p.duration}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
      <button
        type="button"
        className={`chatAssistant__modelBtn chatAssistant__modelBtn_${chatModelId}`}
        onClick={handleClick}
        disabled={disabled}
        title={buttonTitle}
      >
        <i className={AGENT_MODEL_FA_ICON_CLASS[chatModelId]} aria-hidden />
        <span className="chatAssistant__modelBtnLabel">{currentLabel}</span>
        {shouldShowBadge ? (
          <span className="chatAssistant__modelBtnQuotas">
            <span
              className="chatAssistant__modelBtnQuota"
              aria-label={
                quota == null
                  ? 'Загрузка лимита'
                  : remaining === null
                    ? 'Без дневного лимита'
                    : dailyRemaining != null
                      ? `Ежедневные: ${dailyRemaining}`
                      : `Осталось генераций: ${remaining}`
              }
            >
              {dailyBadgeText}
            </span>
            {showBonusBadge ? (
              <span
                className="chatAssistant__modelBtnQuota"
                aria-label={`Бонусные: ${bonusRemaining}`}
              >
                +{bonusRemaining}
              </span>
            ) : null}
          </span>
        ) : null}
        {showLoader ? <span className="chatAssistant__modelBtnLoader" aria-hidden /> : null}
      </button>
    </div>
  );
}
